import { createSlice } from "@reduxjs/toolkit";
import { getJewelleryOrders } from "./thunk";

export type subcategoryType = {
  _id: string;
  category: string;
  name: string;
  image: string;
};

export type styleType = {
  _id: string;
  category: {};
  name: string;
  image: string;
};

export type categoryType = {
  _id: string;
  name: string;
  image: string;
  subcategories: subcategoryType[];
  styles: styleType[];
};

export type metalType = {
  type: String;
  carat: string;
  price: number;
};

export type FilterOptions = {
  search?: string;
  gender?: "male" | "female";
  category?: string;
  subcategory?: string;
  shape?: string;
  onlyDiscounted?: boolean;
}

type initialStateType = {
  jewellery: Object | null;
  jewelleries: Object[];
  jewelleryDiscountModel: Object[];
  selectedProducts: Object[];
  categories: categoryType[];
  subcategories: subcategoryType[];
  styles: styleType[];
  metals: metalType[];
  orders: Object[];
  countsByStatus: {};
  countsPercentagesByMonth: {};
  discounts: Object[];
  errors: {};
  isLoading: boolean;
  isEditing: boolean;
  totalCount: number;
  currentPage: number;
  isDuplicate: boolean;
  filterOptions: FilterOptions;
};

export const initialState: initialStateType = {
  jewellery: null,
  jewelleries: [],
  jewelleryDiscountModel: [],
  selectedProducts: [],
  categories: [],
  subcategories: [],
  styles: [],
  metals: [],
  orders: [],
  countsByStatus: {},
  countsPercentagesByMonth: {},
  discounts: [],
  errors: {},
  isLoading: false,
  isEditing: false,
  totalCount: 0,
  currentPage: 1,
  isDuplicate: false,
  filterOptions: {},
};

const JewellerySlice = createSlice({
  name: "Jewellery",
  initialState,
  reducers: {
    setIsLoading: (state: initialStateType, action: { payload: boolean }) => {
      state.isLoading = action.payload;
    },
    setIsEditing: (state: initialStateType, action: { payload: boolean }) => {
      state.isEditing = action.payload;
    },
    setTotalCount: (state: initialStateType, action: { payload: number }) => {
      state.totalCount = action.payload;
    },

    setCurrentPage: (state: initialStateType, action: { payload: number }) => {
      state.currentPage = action.payload;
    },
    setIsDuplicate: (state: initialStateType, action: { payload: boolean }) => {
      state.isDuplicate = action.payload;
    },

    setFilterOptions: (state: initialStateType, action: { payload: FilterOptions }) => {
      state.filterOptions = action.payload; // Update the filter options based on the payload
    },

    resetFilterOptions: (state) => {
      state.filterOptions = {}
    },

    getJewelleryByIdSuccess: (state: initialStateType, action: any) => {
      state.jewellery = action.payload;
    },
    getJewelleryByIdError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getJewelleryBySlugSuccess: (state: initialStateType, action: any) => {
      state.jewellery = action.payload;
    },
    getJewelleryBySlugError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getJewelleryBySkuSuccess: (state: initialStateType, action: any) => {
      state.jewellery = action.payload;
    },
    getJewelleryBySkuError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    addJewellerySuccess: (state: initialStateType, action: any) => {
      state.jewelleries.unshift(action.payload);
    },
    addJewelleryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getJewelleriesSuccess: (state: initialStateType, action: any) => {
      state.jewelleries = action.payload;
    },
    getJewelleriesError: (state: initialStateType, action: any) => {
      state.errors = action.payload;
    },
    getJewelleryDiscountModelSuccess: (
      state: initialStateType,
      action: any
    ) => {
      // Extract only new products that aren't already in the model
      const newProducts = action.payload.filter(
        (newProduct: any) =>
          !state.jewelleryDiscountModel.some(
            (existingProduct: any) => existingProduct._id === newProduct._id // Replace `_id` if you use a different unique key
          )
      );

      // Update the state by concatenating the new products
      state.jewelleryDiscountModel = [
        ...state.jewelleryDiscountModel,
        ...newProducts,
      ];
    },

    setJewelleryForDiscountModel: (state: initialStateType, action: any) => {
      state.jewelleryDiscountModel = action.payload;
    },
    getJewelleryDiscountModelError: (state: initialStateType, action: any) => {
      state.jewelleryDiscountModel = action.payload;
    },
    updateJewellerySuccess: (state: initialStateType, action: any) => {
      state.jewelleries = state.jewelleries.map((jewellery: any) =>
        jewellery._id === action.payload._id
          ? { ...jewellery, ...action.payload }
          : jewellery
      );
    },
    updateJewelleryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    deleteJewellerySuccess: (state: initialStateType, action: any) => {
      state.jewelleries = state.jewelleries.filter(
        (jewellery: any) => jewellery._id !== action.payload
      );
    },
    deleteJewelleryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },

    // Category
    addCategorySuccess: (state: initialStateType, action: any) => {
      state.categories.unshift(action.payload);
    },
    addCategoryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getCategoriesSuccess: (state: initialStateType, action: any) => {
      state.categories = action.payload;
    },
    getCategoriesError: (state: initialStateType, action: any) => {
      state.errors = action.payload;
    },
    updateCategorySuccess: (state: initialStateType, action: any) => {
      state.categories = state.categories.map((category: any) =>
        category._id === action.payload._id
          ? { ...category, ...action.payload }
          : category
      );
    },
    updateCategoryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    deleteCategorySuccess: (state: initialStateType, action: any) => {
      state.categories = state.categories.filter(
        (category: any) => category._id !== action.payload
      );
    },
    deleteCategoryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },

    // Sub Category
    addSubCategorySuccess: (state: initialStateType, action: any) => {
      state.subcategories.unshift(action.payload);
    },
    addSubCategoryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getSubCategoriesSuccess: (state: initialStateType, action: any) => {
      state.subcategories = action.payload;
    },
    getSubCategoriesError: (state: initialStateType, action: any) => {
      state.errors = action.payload;
    },
    updateSubCategorySuccess: (state: initialStateType, action: any) => {
      state.subcategories = state.subcategories.map((category: any) =>
        category._id === action.payload._id
          ? { ...category, ...action.payload }
          : category
      );
    },
    updateSubCategoryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    deleteSubCategorySuccess: (state: initialStateType, action: any) => {
      state.subcategories = state.subcategories.filter(
        (category: any) => category._id !== action.payload
      );
    },
    deleteSubCategoryError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },

    // Style
    addStyleSuccess: (state: initialStateType, action: any) => {
      state.styles.unshift(action.payload);
    },
    addStyleError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getStylesSuccess: (state: initialStateType, action: any) => {
      state.styles = action.payload;
    },
    getStylesError: (state: initialStateType, action: any) => {
      state.errors = action.payload;
    },
    updateStyleSuccess: (state: initialStateType, action: any) => {
      state.styles = state.styles.map((style: any) =>
        style._id === action.payload._id
          ? { ...style, ...action.payload }
          : style
      );
    },
    updateStyleError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    deleteStyleSuccess: (state: initialStateType, action: any) => {
      state.styles = state.styles.filter(
        (category: any) => category._id !== action.payload
      );
    },
    deleteStyleError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },

    // Metal
    addMetalSuccess: (state: initialStateType, action: any) => {
      state.metals.unshift(action.payload);
    },
    addMetalError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    getMetalsSuccess: (state: initialStateType, action: any) => {
      state.metals = action.payload;
    },
    getMetalsError: (state: initialStateType, action: any) => {
      state.errors = action.payload;
    },
    updateMetalSuccess: (state: initialStateType, action: any) => {
      state.metals = state.metals.map((metal: any) =>
        metal._id === action.payload._id
          ? { ...metal, ...action.payload }
          : metal
      );
    },
    updateMetalError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
    deleteMetalSuccess: (state: initialStateType, action: any) => {
      state.metals = state.metals.filter(
        (metal: any) => metal._id !== action.payload
      );
    },
    deleteMetalError: (state: initialStateType, action: any) => {
      state.errors = action?.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJewelleryOrders.fulfilled, (state: any, action: any) => {
      state.orders = action.payload.orders;
      state.countsByStatus = action.payload.countsByStatus;
      state.countsPercentagesByMonth = action.payload.countsPercentagesByMonth;
    });
    builder.addCase(getJewelleryOrders.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error || "An error occur while fetching orders";
    });
  },
});

export default JewellerySlice.reducer;
export const {
  setCurrentPage,
  setIsLoading,
  setIsEditing,
  getCategoriesError,
  getCategoriesSuccess,
  addCategoryError,
  addCategorySuccess,
  deleteCategoryError,
  deleteCategorySuccess,
  updateCategoryError,
  updateCategorySuccess,
  addSubCategoryError,
  addSubCategorySuccess,
  deleteSubCategoryError,
  deleteSubCategorySuccess,
  getSubCategoriesError,
  getSubCategoriesSuccess,
  updateSubCategoryError,
  updateSubCategorySuccess,
  addStyleError,
  addStyleSuccess,
  deleteStyleError,
  deleteStyleSuccess,
  getStylesError,
  getStylesSuccess,
  updateStyleError,
  updateStyleSuccess,
  addJewelleryError,
  addJewellerySuccess,
  deleteJewelleryError,
  deleteJewellerySuccess,
  getJewelleriesError,
  getJewelleriesSuccess,
  updateJewelleryError,
  updateJewellerySuccess,
  addMetalError,
  addMetalSuccess,
  deleteMetalError,
  deleteMetalSuccess,
  getMetalsError,
  getMetalsSuccess,
  updateMetalError,
  updateMetalSuccess,
  getJewelleryByIdError,
  getJewelleryByIdSuccess,
  getJewelleryBySlugSuccess,
  getJewelleryBySlugError,
  getJewelleryBySkuSuccess,
  getJewelleryBySkuError,
  setTotalCount,
  setJewelleryForDiscountModel,
  setIsDuplicate,
  setFilterOptions,
  resetFilterOptions
} = JewellerySlice.actions;
